<template>

    <popup-modal
        position="center"
        :title="fields.title.toString()"
        @close="$emit('close')"
    >
        <div class="w-672 px-64">
            <label class="block mb-32 text-primary-80 first-letter:uppercase" for="tag-search-input">
                {{ fields.description }}
            </label>

            <div v-if="fetchingUsers" class="pt-16">
                <loading></loading>
            </div>

            <div v-else>
                <div v-for="type in types" class="mb-24">
                    <h2 class="mb-16 font-semibold text-body first-letter:uppercase">
                        {{ fields[`subscribed_${type}_title`] }}
                    </h2>
                    <ul
                        class="rounded overflow-hidden"
                        :class="subscriptionList(type).length ? 'border border-primary-20' : ''"
                    >
                        <li
                            v-for="subscription in subscriptionList(type)"
                            :key="'subscription-' + subscription.id"
                            class="flex justify-between items-center odd:bg-primary-5 px-12 h-48"
                        >
                            <div class="flex items-center gap-8">
                                <avatar
                                    class="cursor-default mr-8 first-letter:uppercase"
                                    :image="subscription.user.image_url"
                                    size="small"
                                >
                                    {{ subscription.user.initials }}
                                </avatar>
                                <span>{{ subscription.user.name }}</span>
                            </div>
                            <div class="flex items-center gap-8">
                                <svg-icon
                                    class="w-18 h-18"
                                    :class="subscription.required ? 'text-secondary' : 'text-primary-40'"
                                    :type="type === 'required' ? 'person-check' : 'person'"
                                ></svg-icon>
                                <svg-icon
                                    v-if="subscription.user.cronofy_id &&
                                        subscription.user.availability_rules &&
                                        subscription.user.availability_rules.length"
                                    class="w-18 h-18 fill-secondary"
                                    type="event-available"
                                ></svg-icon>
                                <svg-icon
                                    v-else
                                    class="w-18 h-18 fill-primary-40"
                                    type="calendar-today"
                                ></svg-icon>
                                <simple-button
                                    class="first-letter:uppercase"
                                    display="danger"
                                    small
                                    :loading="unsubscribing === subscription"
                                    @click="unsubscribeUser(subscription)"
                                >
                                    {{ fields.unsubscribe }}
                                </simple-button>
                            </div>
                        </li>
                    </ul>
                </div>

                <div class="mt-48 mb-32">
                    <h2 class="mb-16 font-semibold text-body first-letter:uppercase">
                        {{ fields.unsubscribed_title }}
                    </h2>
                    <div class="rounded rounded-b-none overflow-hidden border border-primary-20">
                        <simple-input
                            v-model="search"
                            on-focus-class=""
                            :placeholder="$t('form.search.user')"
                            :show-border="false"
                        ></simple-input>
                    </div>
                    <ul class="rounded rounded-t-none" :class="unsubscribed.length ? 'border border-t-0 border-primary-20' : ''">
                        <li
                            v-for="(user, index) in unsubscribed"
                            :key="'unsubscribed-' + user.id"
                            class="flex justify-between items-center odd:bg-primary-5 px-12 h-48"
                            :class="index === unsubscribed.length - 1 ? 'rounded-b' : ''"
                        >
                            <div class="flex items-center gap-8">
                                <avatar
                                    :image="user.image_url"
                                    size="small"
                                    class="cursor-default"
                                >{{ user.initials }}
                                </avatar>
                                <span>{{ user.name }}</span>
                            </div>
                            <div class="flex items-center gap-8">
                                <svg-icon
                                    v-if="user.cronofy_id && user.availability_rules && user.availability_rules.length"
                                    class="w-18 h-18 fill-secondary"
                                    type="event-available"
                                ></svg-icon>
                                <svg-icon
                                    v-else
                                    class="w-18 h-18 fill-primary-40"
                                    type="calendar-today"
                                ></svg-icon>
                                <dropdown-button
                                    :label="fields.subscribe"
                                    :loading="subscribing === user"
                                    :user="user"
                                    @subscribe="subscribeUser(user, $event)"
                                ></dropdown-button>
                            </div>
                        </li>
                    </ul>
                </div>

                <div class="flex items-center justify-end first-letter:uppercase">
                    <simple-button look="primary" display="secondary" @click="$emit('close')">
                        {{ fields.done }}
                    </simple-button>
                </div>
            </div>

        </div>
    </popup-modal>

</template>

<script>
import PopupModal from './PopupModal.vue';
import Application from '../../models/Application';
import Job from '../../models/Job';
import User from '../../models/User';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useAuthenticationStore} from '../../state/modules/authentication';
import {useUsersStore} from '../../state/modules/users';
import * as subscriptions from '../../services/SubscriptionService';
import Subscription from '../../models/Subscription';
import { normalizeJson } from '../../../shared/helpers';
import Avatar from './Avatar.vue';
import SimpleButton from './SimpleButton.vue';
import Loading from './Loading.vue';
import Interview from "../../models/Interview";
import SvgIcon from "./SvgIcon.vue";
import DropdownButton from "./DropdownButton.vue";
import SimpleInput from "../forms/Input.vue";

export default {
    components: {
        SimpleInput,
        DropdownButton,
        SvgIcon,
        'popup-modal': PopupModal,
        'avatar': Avatar,
        'simple-button': SimpleButton,
        'loading': Loading,
    },
    props: {
        resourceTitle: {
            required: true,
            type: String,
        },
        subs: Array,
        target: {
            required: false,
            validator: value => value instanceof Application || value instanceof Job || value instanceof Interview,
        },
        model: {
            type: String
        },
        translations: Object
    },
    data: function () {
        return {
            types: ['required', 'optional'],
            unsubscribing: null,
            subscribing: null,
            search: null,
        }
    },
    computed: {
        ...mapState(useAuthenticationStore, ['currentCompanyId']),
        ...mapState(useUsersStore, ['fetchingUsers']),
        users () {
            return useRepo(User).whereHas('companies', query => query.whereId(this.currentCompanyId)).get();
        },
        subscriptions () {
            if (this.subs)
                return this.subs

            if (this.target)
                return this.target.subscriptions

            return useRepo(Subscription)
                .with('user')
                .where('subable_type', this.model)
                .where('subable_id', null).get();
        },
        requiredSubscriptions() {
            return this.subscriptions.filter(sub => sub.required);
        },
        optionalSubscriptions() {
            return this.subscriptions.filter(sub => !sub.required);
        },
        subscriberIds () {
            return this.subscriptions.map(subscription => subscription.user_id);
        },
        subscribed () {
            return this.users.filter(user => this.subscriberIds.includes(user.id));
        },
        unsubscribed () {
            const search = this.search ? this.search.toLowerCase() : null;
            return this.users
                .filter(user => !this.subscriberIds.includes(user.id))
                .filter(user => !this.search ||
                    (user.first_name.toLowerCase().includes(search) || user.last_name.toLowerCase().includes(search)))
                .sort((a, b) => a.first_name.localeCompare(b.first_name));
        },
        fields() {
            return Object.assign({
                title: this.$t('subscriptions.title_subject', {subject: this.resourceTitle}),
                description: this.$t('subscriptions.description_subject', {subject: this.resourceTitle}),
                subscribed_required_title: this.$t('subscriptions.subscribed_required_title'),
                subscribed_optional_title: this.$t('subscriptions.subscribed_optional_title'),
                subscribed_title: this.$t('subscriptions.subscribed_users'),
                unsubscribe: this.$t('subscriptions.unsubscribe'),
                unsubscribed_title: this.$t('subscriptions.unsubscribed_users'),
                subscribe: this.$t('subscriptions.subscribe'),
                done: this.$t('subscriptions.ready')
            }, this.translations)
        }
    },
    methods: {
        ...mapActions(useUsersStore, ['fetchUsers']),
        subscriptionList(type) {
            if (type === 'required') {
                return this.requiredSubscriptions;
            } else {
                return this.optionalSubscriptions;
            }
        },
        subscribeUser (user, subscribeType) {
            if (!subscribeType) return;

            if (!this.target) {
                return useRepo(Subscription).save({
                        id: this.model + user.id,
                        user_id: user.id,
                        company_id: this.currentCompanyId,
                        subable_id: null,
                        subable_type: this.model,
                        required: subscribeType === 'required'
                    });
            }

            this.subscribing = user;

            if (this.targetType !== "interviews" || !isNaN(this.target.id)) {
                subscriptions.store({ data: {
                    attributes: {
                        required: subscribeType === 'required'
                    },
                    relationships: {
                        user: { data: {
                            type: 'user',
                            id: user.id,
                        }},
                        subable: { data: {
                            type: this.targetType,
                            id: this.target.id,
                        }}
                    }
                }}).then(result => {
                    return useRepo(Subscription).save(normalizeJson(result));
                }).then(inserts => {
                    this.subscribing = null;
                });
            } else {
                useRepo(Subscription).save({
                        user_id: user.id,
                        company_id: this.currentCompanyId,
                        subable_id: null,
                        subable_type: 'interviews',
                        required: subscribeType === 'required'
                    });
            }
        },

        unsubscribeUser (subscription) {
            if (!this.target) {
                return useRepo(Subscription).destroy(subscription.id);
            }

            this.unsubscribing = subscription;
            this.subscribing = null;

            if (subscription.subable_type !== "interviews" || !isNaN(this.target.id)) {
                subscriptions.destroy(subscription.id).then(() => {
                    useRepo(Subscription).destroy(subscription.id);
                }).then(() => {
                    this.unsubscribing = null;
                });
            } else {
                return useRepo(Subscription).destroy(subscription.id);
            }
        }
    },
    created () {
        if (this.target) {
            if (this.target instanceof Application)
                this.targetType = 'applications'
            if (this.target instanceof Job)
                this.targetType = 'jobs'
            if (this.target instanceof Interview)
                this.targetType = 'interviews'
        }
    },
    mounted () {
        this.fetchUsers();
    }
}
</script>
